import { createAddressId, normalizeAddressBook, toAddressSignature } from './addressBookNormalization'
import type { AddressBookEntry, AddressSeedProfile } from './addressBookTypes'
import { parseProfileAddressValue } from '../../profileAddress'

const parseRawAddress = (value: string, profile: AddressSeedProfile, countryFallback: string) => {
  const firstName = profile.firstName.trim()
  const lastName = profile.lastName.trim()
  const parsed = parseProfileAddressValue({
    value,
    countryFallback,
    phoneFallback: profile.phone,
  })

  return {
    firstName,
    lastName,
    addressLine: parsed?.addressLine ?? value.trim(),
    city: parsed?.city ?? '',
    postalCode: parsed?.postalCode ?? '',
    province: parsed?.province ?? '',
    country: parsed?.country ?? countryFallback,
    phone: parsed?.phone ?? profile.phone.trim(),
  }
}

export const buildSeedAddressBook = (profile: AddressSeedProfile, countryFallback: string): AddressBookEntry[] => {
  const entries: AddressBookEntry[] = []

  const addEntry = (
    rawValue: string,
    flags: Pick<AddressBookEntry, 'useShipping' | 'useBilling' | 'defaultShipping' | 'defaultBilling'>,
    fallbackLabel: string,
  ) => {
    const trimmed = rawValue.trim()
    if (!trimmed) return

    const parsed = parseRawAddress(trimmed, profile, countryFallback)
    const signature = toAddressSignature({
      ...parsed,
      country: parsed.country,
    })

    const existing = entries.find((entry) => toAddressSignature(entry) === signature)
    if (existing) {
      existing.useShipping = existing.useShipping || flags.useShipping
      existing.useBilling = existing.useBilling || flags.useBilling
      existing.defaultShipping = existing.defaultShipping || flags.defaultShipping
      existing.defaultBilling = existing.defaultBilling || flags.defaultBilling
      return
    }

    entries.push({
      id: createAddressId(),
      label: fallbackLabel,
      ...parsed,
      useShipping: flags.useShipping,
      useBilling: flags.useBilling,
      defaultShipping: flags.defaultShipping,
      defaultBilling: flags.defaultBilling,
    })
  }

  addEntry(
    profile.shippingAddress,
    {
      useShipping: true,
      useBilling: false,
      defaultShipping: true,
      defaultBilling: false,
    },
    '',
  )

  addEntry(
    profile.billingAddress,
    {
      useShipping: false,
      useBilling: true,
      defaultShipping: false,
      defaultBilling: true,
    },
    '',
  )

  addEntry(
    profile.residentialAddress,
    {
      useShipping: false,
      useBilling: false,
      defaultShipping: false,
      defaultBilling: false,
    },
    '',
  )

  return normalizeAddressBook(entries)
}
